/**
 * Automations module surface — four fixed cards (design 2026-06-26 "Automations Module Revamp").
 *
 * Replaces the retired three-tab redesign (autonomy dials, sign-off-queue tab, run timelines,
 * chat-scoping). Every workspace is seeded with exactly four non-deletable preset automations; this
 * view renders one card per preset kind in canonical order, wires each on/off toggle to the existing
 * PATCH /sub-agents/:id/config endpoint, and routes every output into the Activity Center. There is
 * no create/delete UI and no per-user automation authoring.
 */
import { useEffect, useMemo } from "react";
import type { Automation, PresetKind } from "./redesign/fixtures";
import { useAutomationsList } from "./redesign/useAutomationsUi";
import { useSessionPersistedState } from "@/common/hooks/useSessionPersistedState";
import { PRESET_META, PRESET_ORDER } from "./preset-meta";
import { AutomationCard } from "./AutomationCard";
import { automationsOpenPanelKey, isPresetKindOrNull } from "./automationsSessionKeys";
import "./automations-surface.css";

export function AutomationsSurface({ moduleId }: { moduleId?: string } = {}) {
  const { data: automations = [], isLoading, isError, refetch } = useAutomationsList();
  // Which card's Customize panel is expanded — persisted per module instance so returning to
  // Automations (the chat pane unmounts the whole module on switch) reopens the same panel
  // SYNCHRONOUSLY on the first frame instead of always collapsing (#5571).
  const [customizeKind, setCustomizeKind] = useSessionPersistedState<PresetKind | null>(
    moduleId ? automationsOpenPanelKey(moduleId) : null,
    null,
    { validate: isPresetKindOrNull },
  );

  // Resolve each preset kind to its live row. The roster may also contain legacy/custom rows;
  // the surface only ever shows the four canonical presets, keyed by triggerConfig.kind.
  const byKind = useMemo(() => {
    const m = new Map<PresetKind, Automation>();
    for (const a of automations) {
      if (a.kind && PRESET_META[a.kind] && !m.has(a.kind)) m.set(a.kind, a);
    }
    return m;
  }, [automations]);

  // Self-heal a stale persisted panel id (e.g. the kind no longer resolves to a live row) once the
  // roster is known to be populated — mirrors Kanban's `activeBoardStale` guard. Gating on
  // `automations.length > 0` avoids collapsing a just-restored panel during the one transient-empty
  // window that legitimately exists (roster not yet fetched).
  const customizeStale =
    Boolean(customizeKind) &&
    automations.length > 0 &&
    !byKind.has(customizeKind as PresetKind);
  useEffect(() => {
    if (customizeStale) setCustomizeKind(null);
  }, [customizeStale, setCustomizeKind]);

  return (
    <div className="autosurface">
      <header className="autosurface__head">
        <h1 className="autosurface__title">Automations</h1>
        <p className="autosurface__sub">
          Four assistants that work in the background. Turn each on or off, and
          customize how it works.
        </p>
      </header>

      {isError && automations.length === 0 ? (
        // Cold roster fetch failed — don't render four confidently-"off" cards
        // with dead toggles; say so and offer a retry. When a background
        // refetch fails (live mode polls every 2.5s) react-query keeps the
        // stale data, and stale real statuses beat blanking the grid.
        <div className="autosurface__error" role="alert">
          <p>Couldn't load your automations.</p>
          <button type="button" onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      ) : (
        <div className="autosurface__cards">
          {PRESET_ORDER.map((kind) => (
            <AutomationCard
              key={kind}
              kind={kind}
              automation={byKind.get(kind)}
              loading={isLoading}
              open={customizeKind === kind}
              moduleId={moduleId}
              onCustomize={() =>
                setCustomizeKind((k) => (k === kind ? null : kind))
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
